import { createContext, useContext, useEffect, useRef, useState, type ReactNode, type ReactElement } from 'react'

export interface SupportEvent {
  studentId: string
  change: 'message' | 'read'
  messageId?: string
  senderId?: string
  kind?: 'text' | 'voice' | 'image'
}
export const alertEnglish = {
  title: 'Chat alerts', sound: 'Message sounds', browser: 'Enable browser alerts', enabled: 'Browser alerts enabled',
  denied: 'Browser alerts are blocked. Change the notification permission in your browser settings.',
  unavailable: 'Browser alerts are unavailable here. In-app alerts will still appear.',
  newText: 'New support message', newVoice: 'New voice message', newImage: 'New image message', open: 'Open conversation', dismiss: 'Dismiss alert',
}
export type AlertText = keyof typeof alertEnglish
export const alertArabic: Record<AlertText, string> = {
  title: 'تنبيهات المحادثات', sound: 'صوت الرسائل', browser: 'تفعيل تنبيهات المتصفح', enabled: 'تنبيهات المتصفح مفعّلة',
  denied: 'تنبيهات المتصفح محظورة. غيّر إذن الإشعارات من إعدادات المتصفح.',
  unavailable: 'تنبيهات المتصفح غير متاحة هنا، وستظهر التنبيهات داخل الصفحة.',
  newText: 'رسالة دعم جديدة', newVoice: 'رسالة صوتية جديدة', newImage: 'صورة جديدة', open: 'فتح المحادثة', dismiss: 'إغلاق التنبيه',
}
const english = (key: AlertText): string => alertEnglish[key]
interface Preferences {
  sound: boolean
  setSound: (sound: boolean) => void
  permission: NotificationPermission | 'unavailable'
  enable: () => void
  failed: boolean
  setReading: (threadId: string | null) => void
}
const PreferencesContext = createContext<Preferences | null>(null)

export function SupportAlerts({ userId, event, activeThreadId, openThread, text = english, children }: {
  userId: string; event: SupportEvent | null; activeThreadId: string | null
  openThread: (studentId: string) => void; text?: (key: AlertText) => string; children: ReactNode
}): ReactElement {
  const [readingThreadId, setReadingThreadId] = useState<string | null>(null)
  const [tabId] = useState(() => crypto.randomUUID())
  const delayed = useRef(new Set<ReturnType<typeof setTimeout>>())
  const [sound, setSound] = useState(true)
  const [permission, setPermission] = useState<NotificationPermission | 'unavailable'>('unavailable')
  const [failed, setFailed] = useState(false)
  const [alert, setAlert] = useState<SupportEvent | null>(null)
  const audio = useRef<AudioContext | null>(null)
  const seen = useRef(new Set<string>())
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    try { setSound(localStorage.getItem(`support-sound:${userId}`) !== 'muted') } catch { setSound(true) }
    setPermission(typeof Notification === 'undefined' ? 'unavailable' : Notification.permission)
    const unlock = () => {
      if (typeof AudioContext === 'undefined') return
      audio.current ??= new AudioContext()
      void audio.current.resume().catch(() => setFailed(true))
    }
    const preferences = (event: StorageEvent) => {
      if (event.key === `support-sound:${userId}`) setSound(event.newValue !== 'muted')
    }
    window.addEventListener('storage', preferences)
    document.addEventListener('pointerdown', unlock)
    document.addEventListener('keydown', unlock)
    return () => {
      window.removeEventListener('storage', preferences)
      delayed.current.forEach((timeout) => { clearTimeout(timeout) })
      document.removeEventListener('pointerdown', unlock)
      document.removeEventListener('keydown', unlock)
      clearTimeout(timer.current)
      void audio.current?.close()
      audio.current = null
    }
  }, [userId])
  useEffect(() => {
    const key = `support-focus:${userId}`
    const focus = () => {
      try {
        if (document.visibilityState === 'visible' && document.hasFocus()) {
          localStorage.setItem(key, JSON.stringify({ tab: tabId, reading: activeThreadId === readingThreadId ? readingThreadId : null, at: Date.now() }))
        } else {
          const current: { tab?: string } = JSON.parse(localStorage.getItem(key) ?? '{}')
          if (current.tab === tabId) localStorage.removeItem(key)
        }
      } catch { /* The alert's in-memory fallback remains available without storage. */ }
    }
    focus()
    window.addEventListener('focus', focus)
    window.addEventListener('blur', focus)
    document.addEventListener('visibilitychange', focus)
    return () => {
      window.removeEventListener('focus', focus)
      window.removeEventListener('blur', focus)
      document.removeEventListener('visibilitychange', focus)
    }
  }, [userId, activeThreadId, readingThreadId, tabId])
  useEffect(() => {
    if (!event?.messageId || event.change !== 'message' || event.senderId === userId || seen.current.has(event.messageId)) return
    seen.current.add(event.messageId)
    if (seen.current.size > 200) seen.current = new Set([event.messageId])
    const current = event
    const focusedHere = document.visibilityState === 'visible' && document.hasFocus()
    if (focusedHere) {
      try { localStorage.setItem(`support-focus:${userId}`, JSON.stringify({ tab: tabId, reading: activeThreadId === readingThreadId ? readingThreadId : null, at: Date.now() })) }
      catch { /* Storage is optional for local alerts. */ }
    }
    const notify = () => {
      const key = `support-alert:${userId}`
      try {
        if (localStorage.getItem(key) === current.messageId) return
        const focus: { tab?: string; reading?: string | null; at?: number } = JSON.parse(localStorage.getItem(`support-focus:${userId}`) ?? '{}')
        if (Date.now() - (focus.at ?? 0) < 2000) {
          if (focus.reading === current.studentId) { localStorage.setItem(key, current.messageId ?? ''); return }
          if (focus.tab && focus.tab !== tabId) return
        }
        localStorage.setItem(key, current.messageId ?? '')
      } catch { /* In-memory deduplication still works when browser storage is blocked. */ }
      if (activeThreadId === current.studentId && readingThreadId === current.studentId && document.visibilityState === 'visible' && document.hasFocus()) return
      setAlert(current)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setAlert(null), 10000)
      let soundEnabled = sound
      try { soundEnabled = localStorage.getItem(`support-sound:${userId}`) !== 'muted' } catch { /* Use the current in-memory preference. */ }
      if (soundEnabled && audio.current?.state === 'running') {
        const context = audio.current
        const tone = context.createOscillator()
        const volume = context.createGain()
        tone.connect(volume); volume.connect(context.destination)
        tone.frequency.setValueAtTime(880, context.currentTime)
        tone.frequency.setValueAtTime(660, context.currentTime + 0.09)
        volume.gain.setValueAtTime(0.06, context.currentTime)
        volume.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.22)
        tone.start(); tone.stop(context.currentTime + 0.23)
      }
      if (document.visibilityState === 'hidden' && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const notification = new Notification(text(current.kind === 'image' ? 'newImage' : current.kind === 'voice' ? 'newVoice' : 'newText'), { tag: `support-${current.studentId}` })
          notification.onclick = () => { window.focus(); openThread(current.studentId); notification.close() }
        } catch { setFailed(true) }
      }
    }
    // One alert per browser, even when the same account has several tabs open.
    const dispatch = () => {
      if (navigator.locks) {
        void navigator.locks.request(`support-alert:${userId}`, { ifAvailable: true }, (lock) => { if (lock) notify() })
      } else notify()
    }
    if (focusedHere) dispatch()
    else {
      // Give the foreground tab time to claim/suppress this message before a hidden tab alerts.
      const timeout = setTimeout(() => { delayed.current.delete(timeout); dispatch() }, 150)
      delayed.current.add(timeout)
    }
  }, [event, userId, activeThreadId, readingThreadId, openThread, sound, text, tabId])
  const changeSound = (value: boolean) => {
    setSound(value)
    try { localStorage.setItem(`support-sound:${userId}`, value ? 'on' : 'muted') } catch { setFailed(true) }
  }
  const enable = () => {
    if (typeof Notification === 'undefined') return
    void Notification.requestPermission().then(setPermission).catch(() => setFailed(true))
  }
  return <PreferencesContext.Provider value={{ sound, setSound: changeSound, permission, enable, failed, setReading: setReadingThreadId }}>
    {children}
    {alert && <div role="status" aria-live="polite" dir="auto" className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-sm rounded-xl border border-rule-strong bg-surface p-4 text-ink shadow-lg sm:inset-x-auto sm:end-4 sm:bottom-6">
      <p className="font-semibold">{text(alert.kind === 'image' ? 'newImage' : alert.kind === 'voice' ? 'newVoice' : 'newText')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => { openThread(alert.studentId); setAlert(null) }}>{text('open')}</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAlert(null)}>{text('dismiss')}</button>
      </div>
    </div>}
  </PreferencesContext.Provider>
}

export function SupportAlertSettings({ text = english }: { text?: (key: AlertText) => string }): ReactElement | null {
  const settings = useContext(PreferencesContext)
  if (!settings) return null
  // A menu beside the page title: set once, then out of the way of the inbox.
  return <details className="relative text-sm">
    <summary className="btn btn-secondary cursor-pointer list-none">{text('title')}</summary>
    <div className="absolute end-0 z-20 mt-2 w-72 space-y-3 rounded-lg border border-rule bg-surface p-3 shadow-lg">
      <div className="flex flex-wrap items-center gap-4">
      <label className="flex items-center gap-2"><input type="checkbox" checked={settings.sound} onChange={(event) => settings.setSound(event.target.checked)} />{text('sound')}</label>
      <button type="button" className="btn btn-secondary btn-sm" disabled={settings.permission === 'granted' || settings.permission === 'unavailable' || settings.permission === 'denied'} onClick={settings.enable}>
        {text(settings.permission === 'granted' ? 'enabled' : 'browser')}
      </button>
      </div>
      {(settings.permission === 'denied' || settings.permission === 'unavailable' || settings.failed) && <p className="text-ink-2">{text(settings.permission === 'denied' ? 'denied' : 'unavailable')}</p>}
    </div>
  </details>
}

export function useSupportReading(threadId: string, atBottom: boolean): void {
  const setReading = useContext(PreferencesContext)?.setReading
  useEffect(() => {
    setReading?.(atBottom ? threadId : null)
    return () => setReading?.(null)
  }, [threadId, atBottom, setReading])
}
